import React, { useMemo, useState } from "react";
import { baseUrl, getAccessToken } from "../../../../serverUrl";

const COUNTY_OPTIONS = [
  "nairobi",
  "embu",
  "kiambu",
  "meru",
  "kisii",
  "mombasa",
  "machakos",
  "kisumu",
  "kakamega",
  "kajiado",
  "narok",
  "kilifi",
  "kwale",
  "lamu",
  "tana river",
  "taita taveta",
  "garissa",
  "wajir",
  "mandera",
  "marsabit",
  "isiolo",
  "tharaka-nithi",
  "makueni",
  "nyandarua",
  "kirinyaga",
  "murang'a",
  "turkana",
  "west pokot",
  "samburu",
  "trans-nzoia",
  "uasin gishu",
  "elgeyo-marakwet",
  "nandi",
  "baringo",
  "laikipia",
  "nakuru",
  "kericho",
  "bomet",
  "vihiga",
  "bungoma",
  "busia",
  "siaya",
  "homa bay",
  "migori",
  "nyamira",
  "kitui",
];

const PROPERTY_TYPES = [
  { value: "comercial", label: "Commercial" },
  { value: "residential", label: "Residential" },
  { value: "both", label: "Both" },
];

const UNIT_TYPES = [
  { value: "shop", label: "Shop" },
  { value: "office", label: "Office" },
  { value: "stall", label: "Stall" },
  { value: "apartment_units", label: "Apartment Unit" },
  { value: "studio", label: "Studio" },
  { value: "bungalow", label: "Bungalow" },
  { value: "bedsitter", label: "Bedsitter" },
  { value: "servant_quarters", label: "Servant Quarters" },
  { value: "mansionette", label: "Mansionette" },
];

const PAYMENT_TYPES = [
  { value: "rent", label: "Rent" },
  { value: "service_charge", label: "Service Charge" },
  { value: "rent_and_service_charge", label: "Rent And Service Charge" },
];

const INITIAL_FORM = {
  landlordId: "99",
  apartmentName: "Eldoret Go Down 2026",
  location: "Eldoret",
  county: "uasin gishu",
  propertyType: "comercial",
  defaultUnitType: "shop",
  invoiceDate: "7",
  invoiceGenerationDay: "5",
  defaultPaymentType: "rent",
};

async function handleJsonResponse(response) {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorMessage =
      data?.error || data?.detail || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }

  return data;
}

function prettifyLabel(value) {
  return String(value || "")
    .split(" ")
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word))
    .join(" ");
}

function InputField({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-blue-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-blue-900 outline-none transition focus:border-gray-500"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-blue-700">{label}</span>
      <select
        value={value}
        onChange={onChange}
        className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-blue-900 outline-none transition focus:border-gray-500"
      >
        {options.map((option) => (
          <option key={option.value || option} value={option.value || option}>
            {option.label || prettifyLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ActionButton({ label, onClick, disabled, tone = "dark" }) {
  const toneClasses = {
    dark: "border-gray-900 bg-gray-900 text-white",
    clay: "border-amber-700 bg-amber-700 text-white",
    light: "border-gray-300 bg-white text-blue-900",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`min-h-[52px] rounded-2xl border-2 px-5 py-3 text-sm font-semibold transition ${
        disabled
          ? "cursor-not-allowed border-gray-200 bg-gray-200 text-blue-500"
          : toneClasses[tone] || toneClasses.dark
      }`}
    >
      {label}
    </button>
  );
}

function SummaryCard({ label, value, tone = "stone" }) {
  const toneClasses = {
    stone: "border-gray-200 bg-white text-blue-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
  };

  return (
    <div className={`rounded-3xl border px-4 py-4 ${toneClasses[tone] || toneClasses.stone}`}>
      <p className="text-xs font-semibold uppercase tracking-widest opacity-70">{label}</p>
      <p className="mt-3 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <section className="rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4 md:px-6">
        <h2 className="text-lg font-semibold text-blue-900">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-blue-500">{subtitle}</p> : null}
      </div>
      <div className="px-5 py-5 md:px-6">{children}</div>
    </section>
  );
}

function MessageList({ title, items, emptyMessage, tone = "amber" }) {
  const toneClasses = {
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
  };

  return (
    <div className={`rounded-3xl border px-4 py-4 ${toneClasses[tone] || toneClasses.amber}`}>
      <h3 className="text-sm font-semibold uppercase tracking-widest">{title}</h3>
      {items?.length ? (
        <ul className="mt-3 space-y-2 text-sm leading-6">
          {items.map((item, index) => (
            <li key={`${title}-${index}`}>{typeof item === "string" ? item : JSON.stringify(item)}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm">{emptyMessage}</p>
      )}
    </div>
  );
}

function DataTable({ title, rows, columns, emptyMessage }) {
  return (
    <div className="rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-base font-semibold text-blue-900">{title}</h3>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-blue-600">
            {rows.length} rows
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        {rows.length ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-blue-600"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((row, index) => (
                <tr key={`${title}-${index}`} className="align-top">
                  {columns.map((column) => (
                    <td key={column.key} className="whitespace-pre-wrap px-4 py-3 text-sm text-blue-700">
                      {column.render ? column.render(row, index) : row[column.key] || "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-5 py-6 text-sm text-blue-500">{emptyMessage}</div>
        )}
      </div>
    </div>
  );
}

function formatFieldCoverage(fields = []) {
  return fields.map((field) => `${field.field}: ${field.source}`).join("\n");
}

export default function HistoricalImportPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [ledgerFile, setLedgerFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importData, setImportData] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const hasRequiredInputs = useMemo(() => {
    return Boolean(
      ledgerFile &&
        form.landlordId &&
        form.apartmentName.trim() &&
        form.location.trim() &&
        form.county
    );
  }, [form, ledgerFile]);

  const buildFormData = () => {
    const payload = new FormData();
    payload.append("ledger_file", ledgerFile);
    payload.append("landlord_id", form.landlordId);
    payload.append("apartment_name", form.apartmentName.trim());
    payload.append("location", form.location.trim());
    payload.append("county", form.county);
    payload.append("property_type", form.propertyType);
    payload.append("default_unit_type", form.defaultUnitType);
    payload.append("invoice_date", form.invoiceDate);
    payload.append("invoice_generation_day", form.invoiceGenerationDay);
    payload.append("default_payment_type", form.defaultPaymentType);
    return payload;
  };

  const postImportRequest = async (endpoint) => {
    const token = getAccessToken();
    const response = await fetch(`${baseUrl}/landlord/${endpoint}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: buildFormData(),
    });
    return handleJsonResponse(response);
  };

  const handlePreview = async () => {
    if (!hasRequiredInputs) {
      setErrorMessage("Add the apartment details and workbook first.");
      return;
    }

    setIsPreviewing(true);
    setErrorMessage("");
    setStatusMessage("");
    setImportData(null);

    try {
      const data = await postImportRequest("historical-apartment-import-preview/");
      setPreviewData(data);
      setStatusMessage(
        `Preview ready. The workbook covers ${data?.summary?.coverage_start || "unknown start"} to ${data?.summary?.coverage_end || "unknown end"}.`
      );
    } catch (error) {
      setPreviewData(null);
      setErrorMessage(error.message || "Preview failed.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!hasRequiredInputs) {
      setErrorMessage("Add the apartment details and workbook first.");
      return;
    }

    setIsImporting(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const data = await postImportRequest("historical-apartment-import-execute/");
      setImportData(data);
      setPreviewData((current) => current || data);
      setStatusMessage(
        `Import completed for apartment ${data?.summary?.apartment_name || form.apartmentName}.`
      );
    } catch (error) {
      setErrorMessage(error.message || "Import failed.");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 md:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-3xl border border-blue-200 bg-blue-50 text-blue-900 shadow-xl">
          <div className="grid gap-6 px-6 py-6 md:grid-cols-2 md:px-8 md:py-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                Historical Apartment Import
              </p>
              <h1 className="mt-3 max-w-3xl text-3xl font-semibold tracking-tight md:text-4xl text-blue-900">
                Separate backdated upload flow for workbook-driven apartment setup
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-blue-800">
                This page creates a new apartment for landlord 99, reads every worksheet in the
                uploaded ledger workbook, backdates records from the earliest sheet, keeps SMS
                notifications off, imports payments as completed transactions only, and fills
                missing tenant phone and email with dummy values.
              </p>
            </div>
            <div className="grid gap-4 md:content-start">
              <div className="rounded-3xl border border-blue-100 bg-white px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                  Import Rules
                </p>
                <ul className="mt-3 space-y-2 text-sm leading-6 text-blue-800">
                  <li>SMS remains disabled for this historical import flow.</li>
                  <li>No payment approval rows will be created for this apartment.</li>
                  <li>Dummy tenant phone numbers and emails are generated automatically.</li>
                  <li>Existing apartments are not overwritten; a new apartment is created only once.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <SectionCard
          title="Import Setup"
          subtitle="Upload the Excel workbook and define the apartment metadata that is not present in the ledger."
        >
          <div className="grid gap-4 lg:grid-cols-3">
            <InputField
              label="Landlord ID"
              value={form.landlordId}
              onChange={(event) => setForm((current) => ({ ...current, landlordId: event.target.value }))}
              placeholder="99"
            />
            <InputField
              label="Apartment Name"
              value={form.apartmentName}
              onChange={(event) => setForm((current) => ({ ...current, apartmentName: event.target.value }))}
              placeholder="Eldoret Go Down 2026"
            />
            <InputField
              label="Location"
              value={form.location}
              onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))}
              placeholder="Eldoret"
            />
            <SelectField
              label="County"
              value={form.county}
              onChange={(event) => setForm((current) => ({ ...current, county: event.target.value }))}
              options={COUNTY_OPTIONS}
            />
            <SelectField
              label="Property Type"
              value={form.propertyType}
              onChange={(event) => setForm((current) => ({ ...current, propertyType: event.target.value }))}
              options={PROPERTY_TYPES}
            />
            <SelectField
              label="Default Unit Type"
              value={form.defaultUnitType}
              onChange={(event) => setForm((current) => ({ ...current, defaultUnitType: event.target.value }))}
              options={UNIT_TYPES}
            />
            <InputField
              label="Invoice Date"
              value={form.invoiceDate}
              onChange={(event) => setForm((current) => ({ ...current, invoiceDate: event.target.value }))}
              placeholder="7"
              type="number"
            />
            <InputField
              label="Invoice Generation Day"
              value={form.invoiceGenerationDay}
              onChange={(event) =>
                setForm((current) => ({ ...current, invoiceGenerationDay: event.target.value }))
              }
              placeholder="5"
              type="number"
            />
            <SelectField
              label="Default Payment Type"
              value={form.defaultPaymentType}
              onChange={(event) =>
                setForm((current) => ({ ...current, defaultPaymentType: event.target.value }))
              }
              options={PAYMENT_TYPES}
            />
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[1fr,auto]">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-blue-700">Ledger Workbook</span>
              <input
                type="file"
                accept=".xlsx"
                onChange={(event) => setLedgerFile(event.target.files?.[0] || null)}
                className="w-full rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-blue-700"
              />
              <p className="mt-2 text-sm text-blue-500">
                Upload the workbook containing the monthly sheets, such as December 2025 onward.
              </p>
            </label>

            <div className="flex flex-col justify-end gap-3">
              <ActionButton
                label={isPreviewing ? "Preparing Preview..." : "Preview Historical Import"}
                onClick={handlePreview}
                disabled={!hasRequiredInputs || isPreviewing || isImporting}
                tone="dark"
              />
              <ActionButton
                label={isImporting ? "Creating Records..." : "Execute Import"}
                onClick={handleImport}
                disabled={!hasRequiredInputs || isPreviewing || isImporting}
                tone="clay"
              />
            </div>
          </div>

          {ledgerFile ? (
            <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              Selected workbook: <span className="font-semibold">{ledgerFile.name}</span>
            </div>
          ) : null}

          {statusMessage ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {statusMessage}
            </div>
          ) : null}

          {errorMessage ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
              {errorMessage}
            </div>
          ) : null}
        </SectionCard>

        {previewData ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
              <SummaryCard label="Sheets" value={previewData.summary?.sheet_names?.length || 0} tone="sky" />
              <SummaryCard label="Houses" value={previewData.summary?.house_count || 0} />
              <SummaryCard label="Tenants" value={previewData.summary?.tenant_count || 0} />
              <SummaryCard label="Occupancies" value={previewData.summary?.occupancy_count || 0} />
              <SummaryCard label="Invoices" value={previewData.summary?.invoice_count || 0} />
              <SummaryCard label="Payments" value={previewData.summary?.payment_rows_count || 0} tone="amber" />
            </div>

            <SectionCard
              title="Preview Summary"
              subtitle="Review the inferred structure before any database records are created."
            >
              <div className="grid gap-4 lg:grid-cols-3">
                <MessageList
                  title="Workbook Coverage"
                  items={[
                    `Coverage start: ${previewData.summary?.coverage_start || "-"}`,
                    `Coverage end: ${previewData.summary?.coverage_end || "-"}`,
                    `Sheet names: ${(previewData.summary?.sheet_names || []).join(", ") || "-"}`,
                    `SMS enabled: ${previewData.summary?.sms_notifications_enabled ? "Yes" : "No"}`,
                    `Payment approvals enabled: ${previewData.summary?.payment_approvals_enabled ? "Yes" : "No"}`,
                    `Payment import mode: ${previewData.summary?.payment_record_mode || "-"}`,
                  ]}
                  emptyMessage="No workbook summary available."
                  tone="sky"
                />
                <MessageList
                  title="Warnings"
                  items={previewData.warnings}
                  emptyMessage="No warnings were raised from the workbook preview."
                  tone="amber"
                />
                <MessageList
                  title="Missing Source Data"
                  items={(previewData.missing_data || []).map(
                    (item) =>
                      `${item.model}.${item.field}: ${item.resolution} (${item.count} record(s))`
                  )}
                  emptyMessage="No missing-data items were detected."
                  tone="amber"
                />
              </div>
            </SectionCard>

            <div className="grid gap-6 xl:grid-cols-2">
              <DataTable
                title="Inferred Houses"
                rows={previewData.houses || []}
                emptyMessage="No houses were inferred from the workbook."
                columns={[
                  { key: "house_number", label: "House" },
                  { key: "invoice_period", label: "Invoice Period" },
                  { key: "house_schedule", label: "Schedule" },
                  { key: "billing_cycle_start_month", label: "Cycle Start" },
                  { key: "monthly_rent", label: "Monthly Rent" },
                  { key: "deposit", label: "Deposit" },
                  { key: "unit_type", label: "Unit Type" },
                  {
                    key: "warnings",
                    label: "Notes",
                    render: (row) => (row.warnings?.length ? row.warnings.join("\n") : "-"),
                  },
                ]}
              />
              <DataTable
                title="Occupancies To Create"
                rows={previewData.occupancies || []}
                emptyMessage="No occupancies were inferred from the workbook."
                columns={[
                  { key: "house_number", label: "House" },
                  { key: "tenant_name", label: "Tenant" },
                  { key: "kra_pin", label: "KRA PIN" },
                  { key: "date_in", label: "Date In" },
                  { key: "date_out", label: "Date Out" },
                  {
                    key: "is_active",
                    label: "Status",
                    render: (row) => (row.is_active ? "active" : "expired"),
                  },
                  { key: "opening_balance", label: "Opening Balance" },
                ]}
              />
            </div>

            <DataTable
              title="Mandatory Field Coverage"
              rows={previewData.model_checks || []}
              emptyMessage="No model coverage checks were produced."
              columns={[
                { key: "model", label: "Model" },
                { key: "records_expected", label: "Records" },
                {
                  key: "all_required_fields_covered",
                  label: "Coverage",
                  render: (row) => (row.all_required_fields_covered ? "complete" : "incomplete"),
                },
                {
                  key: "required_fields",
                  label: "Required Fields",
                  render: (row) => formatFieldCoverage(row.required_fields),
                },
              ]}
            />
          </>
        ) : null}

        {importData ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
              <SummaryCard label="Apartment ID" value={importData.summary?.apartment_id || "-"} tone="emerald" />
              <SummaryCard label="Houses Created" value={importData.summary?.houses_created || 0} />
              <SummaryCard label="Tenants Created" value={importData.summary?.tenants_created || 0} />
              <SummaryCard label="Occupancies Created" value={importData.summary?.occupancies_created || 0} />
              <SummaryCard label="Invoices Created" value={importData.summary?.invoices_created || 0} />
              <SummaryCard label="Transactions Created" value={importData.summary?.transactions_created || 0} tone="amber" />
            </div>

            <SectionCard
              title="Created Records"
              subtitle="This is the run-down of what the historical import wrote into the system."
            >
              <div className="grid gap-4 lg:grid-cols-3">
                <MessageList
                  title="Apartment"
                  items={[
                    `ID: ${importData.created?.apartment?.id || "-"}`,
                    `Name: ${importData.created?.apartment?.name || "-"}`,
                    `Location: ${importData.created?.apartment?.location || "-"}`,
                    `County: ${importData.created?.apartment?.county || "-"}`,
                    `SMS enabled: ${importData.summary?.sms_notifications_enabled ? "Yes" : "No"}`,
                    `Payment approvals enabled: ${importData.summary?.payment_approvals_enabled ? "Yes" : "No"}`,
                  ]}
                  emptyMessage="No apartment details available."
                  tone="sky"
                />
                <MessageList
                  title="Import Defaults"
                  items={[
                    `Dummy phones: ${importData.defaults_used?.dummy_phone_numbers ? "enabled" : "disabled"}`,
                    `Dummy emails: ${importData.defaults_used?.dummy_emails ? "enabled" : "disabled"}`,
                    `Payment import mode: ${importData.defaults_used?.payment_record_mode || "-"}`,
                    `Invoice date: ${importData.requested_config?.invoice_date || "-"}`,
                    `Invoice generation day: ${importData.requested_config?.invoice_generation_day || "-"}`,
                  ]}
                  emptyMessage="No import defaults available."
                  tone="sky"
                />
                <MessageList
                  title="Warnings"
                  items={importData.warnings}
                  emptyMessage="No warnings were returned after import."
                  tone="amber"
                />
              </div>
            </SectionCard>

            <div className="grid gap-6 xl:grid-cols-2">
              <DataTable
                title="Created Houses"
                rows={importData.created?.houses || []}
                emptyMessage="No house records were returned."
                columns={[
                  { key: "id", label: "ID" },
                  { key: "house_number", label: "House" },
                  { key: "schedule", label: "Schedule" },
                  { key: "billing_cycle_start_month", label: "Cycle Start" },
                  { key: "rent", label: "Rent" },
                  { key: "deposit", label: "Deposit" },
                ]}
              />
              <DataTable
                title="Created Tenants"
                rows={importData.created?.tenants || []}
                emptyMessage="No tenant records were returned."
                columns={[
                  { key: "id", label: "ID" },
                  { key: "name", label: "Tenant" },
                  { key: "phone_number", label: "Dummy Phone" },
                  { key: "email", label: "Dummy Email" },
                  { key: "kra_pin", label: "KRA PIN" },
                ]}
              />
            </div>

            <DataTable
              title="Created Occupancies"
              rows={importData.created?.occupancies || []}
              emptyMessage="No occupancy records were returned."
              columns={[
                { key: "id", label: "ID" },
                { key: "house_number", label: "House" },
                { key: "tenant_name", label: "Tenant" },
                { key: "status", label: "Status" },
                { key: "date_in", label: "Date In" },
                { key: "date_out", label: "Date Out" },
                { key: "opening_balance", label: "Opening Balance" },
              ]}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
